import {
	Component,
	Input,
	Output,
	EventEmitter,
	OnDestroy,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	forwardRef,
	ViewChild,
	ElementRef,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { Subject } from "rxjs";
import { ImageModalService } from "src/app/_core/services/image-modal.service";

@Component({
    selector: "app-image-upload",
    templateUrl: "./image-upload.component.html",
    styleUrls: ["./image-upload.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ImageUploadComponent),
            multi: true,
        },
    ],
    standalone: false
})
export class ImageUploadComponent implements OnDestroy, ControlValueAccessor {
	private readonly destroy$ = new Subject<void>();

	@ViewChild("fileInput") fileInput!: ElementRef<HTMLInputElement>;

	// Inputs
	@Input() label = "Imagen";
	@Input() required = false;
	@Input() disabled = false;
	@Input() maxSizeMB = 5; // Tamaño máximo en MB
	@Input() acceptedFormats: string[] = [
		"image/jpeg",
		"image/png",
		"image/jpg",
		"image/gif",
		"image/webp",
	];
	@Input() hint = "Seleccione una imagen (JPG, PNG, GIF, WEBP)";
	@Input() dropLabel = "Arrastra una imagen o haz clic para subirla";

	// Estado de arrastre (drag & drop)
	isDragging = false;

	// Outputs
	@Output() imageSelected = new EventEmitter<string>();
	@Output() imageError = new EventEmitter<string>();

	// State
	imageBase64: string | null = null;
	fileName = "";
	fileSize = "";
	errorMessage = "";
	previewUrl: string | null = null;
	isLoading = false;

	// ControlValueAccessor
	private onChange = (_value: string | null): void => {
		// This is intentional
	};
	private onTouched = (): void => {
		// This is intentional
	};

	constructor(
		private readonly cdr: ChangeDetectorRef,
		private readonly imageModalService: ImageModalService,
	) {}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	// ControlValueAccessor implementation
	writeValue(value: string | null): void {
		this.imageBase64 = value;
		if (value) {
			this.previewUrl = value;
			// Establecer fileName cuando se carga imagen existente
			if (!this.fileName) {
				this.fileName = "Imagen cargada";
				this.fileSize = this.estimateImageSize(value);
			}
		} else {
			this.previewUrl = null;
			this.fileName = "";
			this.fileSize = "";
		}
		this.cdr.markForCheck();
	}

	registerOnChange(fn: (value: string | null) => void): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		this.disabled = isDisabled;
		this.cdr.markForCheck();
	}

	/**
	 * Abre el selector de archivos
	 */
	openFileSelector(): void {
		if (!this.disabled) {
			this.fileInput.nativeElement.click();
		}
	}

	/**
	 * Maneja la selección de archivo
	 */
	onFileSelected(event: Event): void {
		const input = event.target as HTMLInputElement;
		this.processFile(input.files?.[0]);
		// Resetear el input para permitir seleccionar el mismo archivo
		input.value = "";
	}

	// ── Drag & drop ───────────────────────────────────────────────────────────
	onDragOver(event: DragEvent): void {
		event.preventDefault();
		event.stopPropagation();
		if (!this.disabled) {
			this.isDragging = true;
			this.cdr.markForCheck();
		}
	}
	onDragLeave(event: DragEvent): void {
		event.preventDefault();
		event.stopPropagation();
		this.isDragging = false;
		this.cdr.markForCheck();
	}
	onDrop(event: DragEvent): void {
		event.preventDefault();
		event.stopPropagation();
		this.isDragging = false;
		if (this.disabled) {
			return;
		}
		this.processFile(event.dataTransfer?.files?.[0]);
	}

	/**
	 * Procesa un archivo (de clic o arrastre): valida, convierte a Base64 y emite.
	 */
	private processFile(file: File | undefined): void {
		if (!file) {
			return;
		}

		// Resetear errores
		this.errorMessage = "";

		// Validar formato
		if (!this.acceptedFormats.includes(file.type)) {
			this.errorMessage = `Formato no válido. Formatos aceptados: ${this.getFormatsText()}`;
			this.imageError.emit(this.errorMessage);
			this.cdr.markForCheck();
			return;
		}

		// Validar tamaño
		const fileSizeMB = file.size / (1024 * 1024);
		if (fileSizeMB > this.maxSizeMB) {
			this.errorMessage = `El archivo es muy grande. Tamaño máximo: ${this.maxSizeMB}MB (actual: ${fileSizeMB.toFixed(2)}MB)`;
			this.imageError.emit(this.errorMessage);
			this.cdr.markForCheck();
			return;
		}

		// Guardar información del archivo
		this.fileName = file.name;
		this.fileSize = this.formatFileSize(file.size);

		// Convertir a Base64
		this.isLoading = true;
		this.cdr.markForCheck();

		const reader = new FileReader();
		reader.onload = () => {
			const base64String = reader.result as string;
			this.imageBase64 = base64String;
			this.previewUrl = base64String;
			this.isLoading = false;

			// Notificar cambios
			this.onChange(base64String);
			this.onTouched();
			this.imageSelected.emit(base64String);
			this.cdr.markForCheck();
		};

		reader.onerror = () => {
			this.errorMessage = "Error al leer el archivo";
			this.isLoading = false;
			this.imageError.emit(this.errorMessage);
			this.cdr.markForCheck();
		};

		reader.readAsDataURL(file);
	}

	/**
	 * Limpia la selección
	 */
	clearSelection(): void {
		this.imageBase64 = null;
		this.previewUrl = null;
		this.fileName = "";
		this.fileSize = "";
		this.errorMessage = "";

		this.onChange(null);
		this.onTouched();

		// Resetear el input
		if (this.fileInput?.nativeElement) {
			this.fileInput.nativeElement.value = "";
		}

		this.cdr.markForCheck();
	}

	/**
	 * Formatea el tamaño del archivo
	 */
	private formatFileSize(bytes: number): string {
		if (bytes === 0) {
			return "0 Bytes";
		}

		const k = 1024;
		const sizes = ["Bytes", "KB", "MB", "GB"];
		const i = Math.floor(Math.log(bytes) / Math.log(k));

		return `${Math.round((bytes / Math.pow(k, i)) * 100) / 100} ${sizes[i]}`;
	}

	/**
	 * Estima el tamaño de una imagen base64
	 */
	private estimateImageSize(base64String: string): string {
		if (!base64String) {
			return "";
		}

		// Remover el prefijo data:image/...;base64,
		const base64Data = base64String.split(",")[1] || base64String;

		// Calcular tamaño aproximado (base64 es ~33% más grande que el binario)
		const bytes = (base64Data.length * 3) / 4;
		return this.formatFileSize(bytes);
	}

	/**
	 * Obtiene el texto de formatos aceptados
	 */
	private getFormatsText(): string {
		return this.acceptedFormats
			.map((format) => format.split("/")[1].toUpperCase())
			.join(", ");
	}

	/**
	 * Muestra la imagen en un dialog modal
	 */
	showImagePreview(): void {
		if (!this.previewUrl) {
			return;
		}

		const title = this.fileName || "Vista previa de imagen";
		this.imageModalService.showImage(this.previewUrl, title).subscribe();
	}
}
